import {createSlice} from '@reduxjs/toolkit';
import {isEmpty} from 'lodash';

const INITIAL_STATE = {
  accessToken: null,
  clientOrgs: [],
  currentOrg: {},
  currentUser: {},
  imageCookieAuthReady: false,
  hasParentOrg: false,
  inscopeOrgs: [],
  isAuthenticated: false,
  isExternalUser: false,
  isShareLinkMode: false,
  orgConfig: {},
  orgs: [],
  parentOrg: null,
  sharedDocumentOrg: null,
  showInitialCopilotScreen: true,
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState: INITIAL_STATE,
  reducers: {
    setOrg: (state, action) => {
      state.currentOrg = action.payload.org;
      state.isExternalUser = isEmpty(action.payload.org);
    },
    setOrgConfig: (state, action) => {
      state.orgConfig = action.payload.orgConfig;
    },
    setOrgs: (state, action) => {
      state.orgs = action.payload.orgs;
      state.isExternalUser = isEmpty(action.payload.orgs);
      // Automatically derive parent and client orgs
      state.parentOrg =
        action.payload.orgs.find(
          (org) => org.orgMetadata?.isParentOrganization
        ) || null;
      state.clientOrgs = action.payload.orgs.filter(
        (org) => !org.orgMetadata?.isParentOrganization
      );
      state.hasParentOrg = !!state.parentOrg;
    },
    setInscopeOrgs: (state, action) => {
      state.inscopeOrgs = action.payload.inscopeOrgs;
    },
    setAccessToken: (state, action) => {
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
    },
    setShowInitialCopilotScreen: (state, action) => {
      state.showInitialCopilotScreen = action.payload.showInitialCopilotScreen;
    },
    setHasParentOrg: (state, action) => {
      state.hasParentOrg = action.payload.hasParentOrg;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload.currentUser;
    },
    setShareLinkMode: (state, action) => {
      state.isShareLinkMode = action.payload.isShareLinkMode;
    },
    setSharedDocumentOrg: (state, action) => {
      state.sharedDocumentOrg = action.payload.org; // { orgId, orgName, documentId }
    },
    clearSharedDocumentOrg: (state) => {
      state.sharedDocumentOrg = null;
    },
    setImageCookieAuthReady: (state, action) => {
      state.imageCookieAuthReady = action.payload.ready;
    },
    reset: () => INITIAL_STATE,
  },
});

// Action creators are generated for each case reducer function
export const {
  reset,
  setOrg,
  setOrgConfig,
  setOrgs,
  setInscopeOrgs,
  setAccessToken,
  setShowInitialCopilotScreen,
  setHasParentOrg,
  setCurrentUser,
  setShareLinkMode,
  setSharedDocumentOrg,
  clearSharedDocumentOrg,
  setImageCookieAuthReady,
} = sessionSlice.actions;

export const selectEffectiveOrg = (state) => {
  if (state.session.isShareLinkMode && state.session.sharedDocumentOrg) {
    return state.session.sharedDocumentOrg;
  }
  return state.session.currentOrg;
};

export const selectIsExternalToEffectiveOrg = (state) => {
  const effectiveOrg = selectEffectiveOrg(state);
  if (!state.session.isAuthenticated || isEmpty(state.session.orgs)) {
    return true;
  }
  // If the effective org's orgId is NOT in the list of user's orgs, they are external
  return !state.session.orgs.some((org) => org.orgId === effectiveOrg?.orgId);
};

export const selectStyleGuide = (state) => {
  const stateOrgConfig = state.session.orgConfig;
  return stateOrgConfig?.documentStyleGuide;
};

/**
 * Whether image-loading prerequisites are satisfied so `Document.jsx` may mount the
 * editor.
 *
 * - **true** = safe to show the editor (`imageCookieAuthReady` is complete).
 * - **false** = keep `EmptyDocument` until `App.jsx` sets `imageCookieAuthReady`.
 */
export const selectImagePrerequisitesReady = (state) => {
  // No session yet: `App.jsx` does not run image-cookie fetch; nothing to wait for.
  if (!state.session.isAuthenticated) return true;

  const effectiveOrg = selectEffectiveOrg(state);

  // No org: `App.jsx` skips the cookie effect; do not block.
  if (isEmpty(effectiveOrg?.orgId)) return true;

  // Ready only after `App.jsx` finishes `getImageCookies()` (or timeout).
  return state.session.imageCookieAuthReady;
};

export default sessionSlice.reducer;
